import { useCallback } from 'react';
import { logSubmitInitiated, logPaginationSubmitError, logScrollWarning } from '../logger/navigationLogger';
import {
  logValidationPassedSubmit,
  logValidationErrorSubmit,
  logValidationFailedSubmit,
} from '../logger/validationLogger';
import { toSdkLoggableError } from '../../types/shared';
import { trackValidationError } from '../telemetry/pageTelemetry';
import { trackSessionSummary } from '../telemetry/sessionTelemetry';
import { findQuestionElement } from '../utils';
import { validatePagesByIndices, validateActivePathPages } from '../utils/validatePagesByIndices';
import { getDefaultEndPageId } from '../utils/navigationState';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { trackLogicSessionSummary } from '../../surveyLogic/telemetry/logicMatchTelemetry';
import { applyMcqDefaults } from '../utils/buildInitialAnswers';
import { pushPageHistory } from '../utils/pageHistory';
import type { SubmitOptions, SurveyPaginationContext } from '../types';

export function useOnSubmit(context: SurveyPaginationContext) {
  const {
    survey,
    pages,
    values,
    submit,
    currentPageIndex,
    setCurrentPageIndex,
    setValidationErrors,
    setPhase,
    trackPageTime,
    trackOptionalSkips,
    checkBacktrackChanges,
    totalQuestionsCount,
    paginationRefs,
    customFieldValues,
  } = context;

  return useCallback(
    async (options?: SubmitOptions) => {
      if (!survey) return;

      const answersToSubmit = applyMcqDefaults(
        pages,
        options?.answersOverride ?? values
      );

      const refs = paginationRefs.current;
      if (!refs) return;

      logSubmitInitiated();
      let validation = null;
      try {
        if (!options?.skipValidation) {
          if (options?.pageIndicesToValidate) {
            validation = validatePagesByIndices(
              pages,
              options.pageIndicesToValidate,
              answersToSubmit,
              survey.language
            );
          } else {
            validation = validateActivePathPages(
              pages,
              answersToSubmit,
              survey.language,
              customFieldValues
            );
          }
        }
        if (!validation) {
          logValidationPassedSubmit();
        }
      } catch (err) {
        logValidationFailedSubmit(toSdkLoggableError(err));
      }

      if (validation) {
        logValidationErrorSubmit(validation);

        const attempt = (refs.validationAttempt[validation.errorPageIndex] || 0) + 1;
        refs.validationAttempt[validation.errorPageIndex] = attempt;
        refs.validationErrorsCount += 1;
        refs.validationHistory.push({
          pageIndex: validation.errorPageIndex,
          errors: validation.errors,
          attemptNumber: attempt,
        });

        trackValidationError(validation.errorPageIndex, validation.errors, attempt);

        setValidationErrors(validation.errors);
        if (validation.errorPageIndex !== currentPageIndex) {
          pushPageHistory(refs, currentPageIndex, validation.errorPageIndex);
        }
        setCurrentPageIndex(validation.errorPageIndex);
        setPhase({ kind: 'content', pageIndex: validation.errorPageIndex });
        setTimeout(() => {
          try {
            const element = findQuestionElement(validation.firstErrorId);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } catch (err) {
            logScrollWarning('Failed to scroll validation error element into view', toSdkLoggableError(err));
          }
        }, 100);
        return;
      }

      setValidationErrors({});

      delete refs.validationAttempt[currentPageIndex];

      trackPageTime(currentPageIndex);
      trackOptionalSkips(currentPageIndex);
      checkBacktrackChanges(currentPageIndex);

      try {
        const result = await submit(answersToSubmit);
        if (result && typeof result === 'object' && 'success' in result && result.success) {
          const allQuestions = pages.flatMap(page => page.questions);
          const logicContext = buildLogicContext(answersToSubmit, allQuestions, customFieldValues);
          const endPageId = getDefaultEndPageId(survey, logicContext);
          if (endPageId) {
            setPhase({ kind: 'end', endPageId });
          }

          if (!refs.hasLoggedSummary) {
            trackLogicSessionSummary(
              refs,
              survey,
              pages,
              allQuestions,
              answersToSubmit,
              customFieldValues,
              endPageId
            );

            const totalDurationMs = performance.now() - refs.sessionStartTime;

            const skippedQuestionIds: string[] = [];
            pages.forEach(page => {
              page.questions.forEach(question => {
                if (!question.required) {
                  const val = answersToSubmit[question.id];
                  if (val === undefined || val === '') {
                    skippedQuestionIds.push(question.id);
                  }
                }
              });
            });
            const skippedQuestionsCount = skippedQuestionIds.length;

            trackSessionSummary(
              true,
              refs.maxVisitedPageIndex,
              totalDurationMs,
              totalQuestionsCount,
              totalQuestionsCount - skippedQuestionsCount,
              skippedQuestionsCount,
              skippedQuestionIds,
              refs.validationErrorsCount,
              refs.backtrackCount,
              refs.validationHistory
            );
            refs.hasLoggedSummary = true;
          }
        }
      } catch (err) {
        logPaginationSubmitError(toSdkLoggableError(err));
      }
    },
    [
      survey,
      pages,
      values,
      submit,
      currentPageIndex,
      setCurrentPageIndex,
      setValidationErrors,
      setPhase,
      trackPageTime,
      trackOptionalSkips,
      checkBacktrackChanges,
      totalQuestionsCount,
      paginationRefs,
      customFieldValues,
    ]
  );
}
